import { RefObject, useCallback, useEffect, useRef } from 'react';
import type { Toast } from 'primereact/toast';

/** How long a focus success toast stays before it auto-dismisses (then fades out). */
export const FOCUS_TOAST_LIFE_MS = 4000;

/**
 * Shows a focus success toast that auto-dismisses after ~4s.
 *
 * The focus toasts render through a `<Toast content={...}>` template, and PrimeReact skips its
 * internal `<ToastMessage>` — and with it the `life`-based auto-dismiss timer — whenever a
 * Toast-level content template is used. So a `life` on the message is ignored and the toast would
 * stay until closed by hand. This drives dismissal ourselves: after the delay we `clear()` the
 * toast, which plays PrimeReact's exit fade. The timer resets on each call so a rapid second toast
 * doesn't get cut short by the first one's timer.
 */
export function useFocusToast(toastRef: RefObject<Toast>, life: number = FOCUS_TOAST_LIFE_MS) {
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Clear a pending dismissal if the owner unmounts before it fires.
  useEffect(() => () => clearTimeout(timer.current), []);
  return useCallback(
    (summary: string, detail = '') => {
      // One visible toast at a time: replace the previous message instead of stacking under it.
      toastRef.current?.clear();
      toastRef.current?.show({ severity: 'success', summary, detail });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => toastRef.current?.clear(), life);
    },
    [toastRef, life],
  );
}
